// src/utils/storageService.js
// Servizi Firebase Storage (upload/elimina/sposta/lista/uso) + helpers compatibili con Sidebar

import {
  getStorage,
  ref,
  listAll,
  getMetadata,
  getDownloadURL,
  uploadBytesResumable,
  deleteObject,
} from 'firebase/storage';

const storage = getStorage();

// Normalizza nome file su Storage ma senza cambiare il nome visualizzato
export function makeStorageSafeName(name) {
  return (name || '').replace(/\s+/g, '_');
}

/**
 * Upload con progress callback - compatibile con due firme:
 * 1) uploadFileWithMetadata(file, { storagePath, displayName, visualName, ownerUid, tags, ... })
 * 2) uploadFileWithMetadata({ file, targetPrefix, onProgress, metadata })
 */
export function uploadFileWithMetadata(fileOrOpts, maybeOpts = {}) {
  // Riconosco la firma
  const isLegacy = fileOrOpts && (fileOrOpts instanceof Blob || fileOrOpts instanceof File);
  const file = isLegacy ? fileOrOpts : fileOrOpts.file;
  if (!file) return Promise.reject(new Error('Nessun file da caricare'));

  // Dati
  const onProgress = isLegacy ? null : fileOrOpts.onProgress || null;

  // Costruisco path
  let storagePath;
  if (isLegacy) {
    storagePath =
      maybeOpts?.storagePath ||
      `${(maybeOpts?.targetPrefix || 'uploads/')}${Date.now()}_${makeStorageSafeName(file.name)}`;
  } else {
    const targetPrefix = fileOrOpts.targetPrefix || 'uploads/';
    storagePath = `${targetPrefix}${Date.now()}_${makeStorageSafeName(file.name)}`;
  }

  // Metadati storage (contentType/metadata raw)
  const rawMeta = isLegacy ? (maybeOpts?.metadata || {}) : (fileOrOpts?.metadata || {});
  const storageRef = ref(storage, storagePath);

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, rawMeta);

    task.on(
      'state_changed',
      (snap) => {
        if (onProgress) {
          const pct = (snap.bytesTransferred / snap.totalBytes) * 100;
          onProgress(Math.round(pct));
        }
      },
      (err) => reject(err),
      async () => {
        try {
          const [url, meta] = await Promise.all([getDownloadURL(storageRef), getMetadata(storageRef)]);
          resolve({
            path: storagePath,
            downloadURL: url,
            size: meta.size || file.size || 0,
            meta,
          });
        } catch (e) {
          reject(e);
        }
      }
    );
  });
}

/** Ottiene un downloadURL a partire dal path completo su Storage */
export async function getDownloadURLByPath(path) {
  if (!path) throw new Error('Path mancante');
  const r = ref(storage, path);
  return getDownloadURL(r);
}

/** Elimina file - accetta stringa path o oggetto {storagePath} */
export async function deleteFile(pathOrObject) {
  const path =
    typeof pathOrObject === 'string'
      ? pathOrObject
      : pathOrObject?.storagePath || pathOrObject?.path;
  if (!path) throw new Error('Path mancante per deleteFile');
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
}

/** Sposta file: copia+elimina (Storage non supporta move atomico) */
export async function moveFile(oldPath, newPrefix) {
  const oldRef = ref(storage, oldPath);
  const url = await getDownloadURL(oldRef);
  const resp = await fetch(url);
  const blob = await resp.blob();
  const filename = oldPath.split('/').pop();
  const newPath = `${newPrefix}${filename}`;
  const newRef = ref(storage, newPath);
  await uploadBytesResumable(newRef, blob);
  await deleteObject(oldRef);
  return newPath;
}

/** Lista file sotto un prefisso (flat) */
export async function listFiles(prefix) {
  const r = ref(storage, prefix);
  const { items } = await listAll(r);
  const out = await Promise.all(
    items.map(async (it) => {
      const [meta, url] = await Promise.all([getMetadata(it), getDownloadURL(it)]);
      return {
        fullPath: it.fullPath,
        path: it.fullPath,
        name: meta.name, // nome su storage (può includere underscore)
        size: meta.size || 0,
        contentType: meta.contentType || '',
        updated: meta.updated || meta.timeCreated,
        url,
        downloadURL: url, // alias per compatibilità con Sidebar
      };
    })
  );
  // ordina alfabeticamente per nome visualizzato
  return out.sort((a, b) => a.name.localeCompare(b.name, 'it'));
}

/** Stima uso spazio: somma size dei file sotto i prefissi noti */
export async function getStorageUsage(prefixes = ['uploads/']) {
  let total = 0;
  for (const p of prefixes) {
    const r = ref(storage, p);
    const { items } = await listAll(r);
    const metas = await Promise.all(items.map((it) => getMetadata(it)));
    total += metas.reduce((s, m) => s + (m.size || 0), 0);
  }
  return total; // bytes
}
